import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ApiService } from './api.service';
import { LoginCredentials, User } from '../../shared/models';
import { BusinessContextService } from './business-context.service';

const SESSION_KEY = 'dentium_session';
const USER_KEY = 'dentium_user';      // cache del user en localStorage
const DEVICE_KEY = 'dentium_device_id';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private apiService = inject(ApiService);
    private router = inject(Router);
    private businessContextService = inject(BusinessContextService);

    // ── State ────────────────────────────────────────────────────────────────
    private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasSession());
    isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

    private currentUserSubject = new BehaviorSubject<User | null>(this.loadCachedUser());
    currentUser$ = this.currentUserSubject.asObservable();

    // ── Helpers ──────────────────────────────────────────────────────────────
    private hasSession(): boolean {
        return !!localStorage.getItem(SESSION_KEY);
    }

    /** Lee el usuario cacheado en localStorage para evitar pérdida al refrescar */
    private loadCachedUser(): User | null {
        try {
            const raw = localStorage.getItem(USER_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private getDeviceId(): string {
        let id = localStorage.getItem(DEVICE_KEY);
        if (!id) {
            id = `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            localStorage.setItem(DEVICE_KEY, id);
        }
        return id;
    }

    private setSession(response: any) {
        const token = response.token || response.access_token;
        if (token) localStorage.setItem('auth_token', token);

        // Marcar sesión activa
        localStorage.setItem(SESSION_KEY, '1');
        this.isAuthenticatedSubject.next(true);

        // Cachear usuario para que esté disponible tras refresh
        const user: User | null = response.user || null;
        if (user) {
            localStorage.setItem(USER_KEY, JSON.stringify(user));
            this.currentUserSubject.next(user);
        }
    }

    // ── Public API ────────────────────────────────────────────────────────────

    login(credentials: LoginCredentials): Observable<any> {
        const payload = {
            identifier: credentials.identifier,
            password: credentials.password,
            deviceId: this.getDeviceId(),
            rememberMe: credentials.rememberMe ?? false,
        };
        return this.apiService.post<any>('auth/login', payload).pipe(
            tap(response => this.setSession(response))
        );
    }

    register(data: any): Observable<any> {
        return this.apiService.post<any>('auth/register', data).pipe(
            tap(response => this.setSession(response))
        );
    }

    logout() {
        this.apiService.post('auth/logout', {}).subscribe({ error: () => { } });
        this.clearSession();
    }

    /** Limpia la sesión solo en el cliente (sin llamar a la API) */
    clearSession() {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem('auth_token');
        localStorage.removeItem(USER_KEY);
        this.isAuthenticatedSubject.next(false);
        this.currentUserSubject.next(null);
        this.businessContextService.clearContext();
        this.router.navigate(['/login']);
    }

    /** Recarga el perfil del usuario desde la API (útil tras refresh) */
    getAccount(): Observable<any> {
        return this.apiService.get<any>('auth/me').pipe(
            tap((response: any) => {
                const user = response.user || response;
                localStorage.setItem(USER_KEY, JSON.stringify(user));
                this.currentUserSubject.next(user);
                this.isAuthenticatedSubject.next(true);
                localStorage.setItem(SESSION_KEY, '1');
            })
        );
    }

    getMyBusinesses(): Observable<any> {
        return this.apiService.get<any>('auth/my-businesses');
    }

    // ── Getters ───────────────────────────────────────────────────────────────

    get currentUserValue(): User | null {
        return this.currentUserSubject.value;
    }

    /** Verdadero si el usuario actual es SuperAdmin — funciona incluso tras refresh */
    get isSuperAdmin(): boolean {
        return this.currentUserSubject.value?.isSuperAdmin === true;
    }

    hasPermission(permission: string): boolean {
        const user = this.currentUserValue;
        if (!user) return false;
        if (user.isSuperAdmin) return true;
        const perms: string[] = [...(user.permissions ?? [])];
        return perms.includes(permission);
    }
}
